import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';
import { Toast, ToastItem } from './common/Toast';

/**
 * Composant qui vérifie si un paramètre 'phone' est présent dans l'URL
 * et effectue une authentification directe si c'est le cas
 */
const DirectAuthHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { directAuth, isAuthenticated, token } = useAuth();
  const [toastInfo, setToastInfo] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);


  useEffect(() => {
    const checkPhoneParam = async () => {
      // Récupérer les paramètres de l'URL
      const searchParams = new URLSearchParams(location.search);
      const phone = searchParams.get('phone');

      // Si le paramètre phone existe et que l'utilisateur n'est pas déjà authentifié
      if (phone && !isAuthenticated) {
        // Validation du format du numéro de téléphone en utilisant authService
        if (!authService.validatePhoneNumber(phone)) {
          setToastInfo({
            message: 'Format de numéro de téléphone invalide. Formats acceptés: 6XXXXXXXX, 2376XXXXXXXX, +2376XXXXXXXX, 002376XXXXXXXX',
            type: 'error'
          });
          return;
        }
        try {
          // Tenter l'authentification directe
          const response = await directAuth(phone);
          
          if (response && response.status === 'success' && response.data) {
            // Stocker manuellement le token dans localStorage
            if (response.data.token) {
              // Stocker directement le token
              authService.setToken(response.data.token);
              
              // Forcer une redirection vers la page d'accueil avec rechargement complet
              setTimeout(() => {
                // Utiliser navigate pour préserver le contexte mobile/desktop
                // et supprimer le paramètre phone de l'URL
                navigate('/', { replace: true });
                
                // Si navigate ne fonctionne pas correctement (problème de contexte React),
                // on peut utiliser un rechargement complet après un court délai
                setTimeout(() => {
                  if (window.location.search.includes('phone')) {
                    window.location.href = window.location.origin;
                  }
                }, 500);
              }, 1000);
            }
            
            // Authentification réussie
            setToastInfo({
              message: 'Authentification réussie ! Redirection...',
              type: 'success'
            });
          } else {
            // Échec de l'authentification
            setToastInfo({
              message: response?.message || 'Échec de l\'authentification directe',
              type: 'error'
            });
          }
        } catch (error) {
          // Erreur lors de l'authentification
          setToastInfo({
            message: error instanceof Error ? error.message : 'Une erreur est survenue',
            type: 'error'
          });
        }
      }
    };

    checkPhoneParam();
  }, [location, directAuth, isAuthenticated, navigate]);

  // Fermeture du toast
  const handleCloseToast = () => {
    setToastInfo(null);
  };

  // Rendu du composant (uniquement le toast si nécessaire)
  return (
    <>
      {toastInfo && (
        <Toast
          message={toastInfo.message}
          type={toastInfo.type}
          onClose={handleCloseToast}
          duration={3000}
          position="top-center"
        />
      )}
    </>
  );
};

export default DirectAuthHandler;
